import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { expandHalfHourStarts } from '../lib/slots';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Loader2, CalendarClock, Clock, Check, AlertTriangle, ArrowLeft } from 'lucide-react';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_TYPE_LABELS = {
  prueba: 'Clase de Prueba (30 min)',
  individual: 'Clase Individual (50 min)',
  intensivo: 'Paquete Intensivo',
};

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export default function ReschedulePage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [{ data: b }, { data: a }] = await Promise.all([
          axios.get(`${API}/bookings/by-token/${token}`),
          axios.get(`${API}/availability`),
        ]);
        setBooking(b);
        setAvailability(a);
      } catch (e) {
        setError(e.response?.data?.detail || 'No pudimos encontrar tu reserva. Es posible que el enlace haya caducado.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token]);

  const availableTimes = useMemo(() => {
    if (!selectedDate || !availability) return [];
    const key = String(selectedDate.getDay());
    const cfg = availability.weekly?.[key];
    const dateStr = formatLocalDate(selectedDate);
    const opened = availability.opened_slots?.[dateStr] || [];
    const taken = new Set([
      ...(availability.booked_slots?.[dateStr] || []),
      ...(availability.blocked_slots?.[dateStr] || []),
    ]);
    // Allow the student's own current slot (they might reselect it accidentally)
    if (booking && booking.date === dateStr) taken.delete(booking.time);
    const baseSlots = cfg && cfg.enabled ? (cfg.slots || []) : [];
    const merged = expandHalfHourStarts([...baseSlots, ...opened]);
    return merged.filter((s) => !taken.has(s));
  }, [selectedDate, availability, booking]);

  const isDateDisabled = (date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    if (!availability) return true;
    const dateStr = formatLocalDate(date);
    if ((availability.blocked_dates || []).includes(dateStr)) return true;
    const key = String(date.getDay());
    const cfg = availability.weekly?.[key];
    const weeklyEnabled = cfg && cfg.enabled && (cfg.slots || []).length > 0;
    const hasOpened = (availability.opened_slots?.[dateStr] || []).length > 0;
    if (!weeklyEnabled && !hasOpened) return true;
    return false;
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Selecciona una nueva fecha y hora');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/bookings/by-token/${token}/reschedule`,
        { date: formatLocalDate(selectedDate), time: selectedTime }
      );
      setBooking({ ...booking, date: data.date, time: data.time, status: 'rescheduled' });
      setSuccess(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reprogramar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#E8F5FA] to-white">
        <Seo title="Reprogramar tu clase" path="/reprogramar" noindex />
        <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#E8F5FA] to-white px-4">
        <Seo title="Reprogramar tu clase" path="/reprogramar" noindex />
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl">
          <AlertTriangle className="w-14 h-14 text-[#F5A6A4] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#4A4A4A] mb-2">Enlace no válido</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold shadow"
          >
            Volver a la web
          </button>
        </div>
      </div>
    );
  }

  const hoursUntil = booking?.hours_until ?? 0;
  const tooClose = hoursUntil < 24;
  const isCancelled = booking?.status === 'cancelled';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8F5FA] to-white py-10 px-4">
      <Seo title="Reprogramar tu clase" path="/reprogramar" noindex />
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#7DBDD9] mb-6"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Volver a la web
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white p-6">
            <div className="flex items-center gap-3 mb-2">
              <CalendarClock className="w-6 h-6" />
              <h1 className="text-2xl font-bold">Reprogramar tu clase</h1>
            </div>
            <p className="opacity-90 text-sm">Hola {booking.name}, elige una nueva fecha y hora.</p>
          </div>

          <div className="p-6">
            {/* Current booking summary */}
            <div className="bg-[#FFF7F6] border border-[#F5A6A4]/40 rounded-2xl p-4 mb-6">
              <p className="text-xs font-semibold text-[#F5A6A4] uppercase tracking-wide mb-2">Reserva actual</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#4A4A4A]">
                <span><strong>Clase:</strong> {CLASS_TYPE_LABELS[booking.class_type] || booking.class_type}</span>
                <span><strong>Fecha:</strong> {booking.date}</span>
                <span><strong>Hora:</strong> {booking.time}</span>
              </div>
            </div>

            {isCancelled && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-red-700 text-sm">
                Esta reserva ha sido cancelada y ya no puede reprogramarse.
              </div>
            )}

            {tooClose && !isCancelled && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-6 text-yellow-800 text-sm">
                <strong>Faltan menos de 24h para tu clase.</strong> Por política, la reprogramación online no está disponible. Por favor, contacta directamente con Cecilia en{' '}
                <a href="mailto:spanischenlared@gmail.com" className="underline">spanischenlared@gmail.com</a>.
              </div>
            )}

            {!tooClose && !isCancelled && !success && (
              <>
                <p className="text-sm font-semibold text-[#4A4A4A] mb-3">Selecciona una nueva fecha:</p>
                <div className="bg-gray-50 rounded-2xl p-3 mb-5">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { setSelectedDate(d); setSelectedTime(null); }}
                    disabled={isDateDisabled}
                    className="rounded-md border-0 mx-auto"
                  />
                </div>

                {selectedDate && (
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-[#4A4A4A] mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Horas disponibles:
                    </p>
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                      {availableTimes.map((t) => (
                        <button
                          key={t}
                          onClick={() => setSelectedTime(t)}
                          data-testid={`time-${t}`}
                          className={`py-2 rounded-lg text-sm font-medium transition-all ${
                            selectedTime === t
                              ? 'bg-[#7DBDD9] text-white shadow'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    {availableTimes.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-2">No hay horas disponibles ese día. Prueba con otra fecha.</p>
                    )}
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={saving || !selectedDate || !selectedTime}
                  data-testid="submit-reschedule-btn"
                  className="w-full py-3.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Guardando…</>
                  ) : (
                    <><Check className="w-5 h-5" /> Confirmar nueva fecha</>
                  )}
                </button>
              </>
            )}

            <AnimatePresence>
              {success && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-6"
                  data-testid="reschedule-success"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#4A4A4A] mb-2">¡Todo listo!</h2>
                  <p className="text-gray-600 mb-1">Tu clase se ha reprogramado a:</p>
                  <p className="text-xl font-bold text-[#7DBDD9]">{booking.date} · {booking.time}</p>
                  <p className="text-sm text-gray-500 mt-4">Recibirás un email de confirmación con la nueva invitación.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
